import { Subject, Observable } from 'rxjs';

import { Component, ElementRef, HostListener, OnDestroy } from '@angular/core';

import { calculateMovement, SnRefreshState } from './pull-to-refresh.utils';

@Component({
  selector: 'sn-pull-to-refresh',
  templateUrl: './pull-to-refresh.component.html',
  styleUrls: ['./pull-to-refresh.component.scss'],
})
export class SnPullToRefreshComponent implements OnDestroy {
  public refreshMovement = 0;
  public refreshProgress = 0;
  public refreshThreshold = 80;

  private _refreshState = SnRefreshState.None;

  private _startY: number;

  private readonly _container: HTMLElement;
  private readonly _refresh$ = new Subject<void>();

  constructor(elementRef: ElementRef) {
    this._container = elementRef.nativeElement;
  }

  get refresh$(): Observable<void> {
    return this._refresh$.asObservable();
  }

  get isPulling(): boolean {
    return this.refreshState === SnRefreshState.Pulling;
  }

  get isUpdating(): boolean {
    return this.refreshState === SnRefreshState.Updating;
  }

  get refreshState(): SnRefreshState {
    return this._refreshState;
  }

  set refreshState(value: SnRefreshState) {
    if (value === SnRefreshState.None) {
      this.refreshMovement = 0;
      this.refreshProgress = 0;
    }

    this._refreshState = value;
  }

  public ngOnDestroy(): void {
    this._refresh$.complete();
  }

  public refreshCompleted(): void {
    this.refreshState = SnRefreshState.None;
  }

  @HostListener('touchstart', ['$event'])
  public onTouchStart(event: TouchEvent): void {
    if (this.refreshState !== SnRefreshState.None) {
      return;
    }

    this._startY = event.touches[0].clientY;

    if (this._container.scrollTop === 0) {
      this.refreshMovement = 0;
      this.refreshProgress = 0;
    }
  }

  @HostListener('touchmove', ['$event'])
  public onTouchMove(event: TouchEvent): void {
    if (this._container.scrollTop > 0) {
      return;
    }

    let movement = event.touches[0].clientY - this._startY;

    if (movement > 0 && this.refreshState !== SnRefreshState.Pulling) {
      movement = 0;
      this._startY = event.touches[0].clientY;
      this.refreshState = SnRefreshState.Pulling;
    }

    if (this.refreshState === SnRefreshState.Pulling) {
      event.preventDefault();
    }

    if (movement > 0) {
      this.refreshMovement = Math.min(calculateMovement(movement, this.refreshThreshold));
      this.refreshProgress = Math.min(this.refreshMovement / this.refreshThreshold, 1);
    } else if (this.refreshMovement > 0) {
      this.refreshMovement = 0;
      this.refreshProgress = 0;
    }
  }

  @HostListener('touchend')
  public onTouchEnd(): void {
    if (this.refreshProgress === 1) {
      this.refreshState = SnRefreshState.Updating;
      this.refreshMovement = this.refreshThreshold;
      this._refresh$.next();
    } else {
      this.refreshState = SnRefreshState.None;
    }
  }
}
